/**
 * Sistema de logging estruturado para a aplicação
 * Suporte a diferentes níveis de log e transporte para diferentes ambientes
 */

import { safeStringify } from "./safeStringify";

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  FATAL = 4,
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  context?: string;
  userId?: string;
  sessionId?: string;
  userAgent?: string;
  url?: string;
  data?: any;
  error?: Error;
  stack?: string;
}

export interface LogTransport {
  log(entry: LogEntry): void;
}

class ConsoleTransport implements LogTransport {
  log(entry: LogEntry): void {
    const levelName = LogLevel[entry.level];
    const timestamp = entry.timestamp.toISOString();
    const prefix = `[${timestamp}] ${levelName}`;

    const logData = {
      message: entry.message,
      context: entry.context,
      data: entry.data,
      error: entry.error?.message,
      stack: entry.error?.stack,
      url: entry.url,
      userId: entry.userId,
      sessionId: entry.sessionId,
    };

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(`${prefix}:`, logData);
        break;
      case LogLevel.INFO:
        console.info(`${prefix}:`, logData);
        break;
      case LogLevel.WARN:
        console.warn(`${prefix}:`, logData);
        break;
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        console.error(`${prefix}:`, logData);
        break;
    }
  }
}

class RemoteTransport implements LogTransport {
  private endpoint: string;
  private buffer: LogEntry[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private readonly batchSize = 10;
  private readonly flushInterval = 5000; // 5 segundos

  constructor(endpoint: string) {
    this.endpoint = endpoint;
    this.startPeriodicFlush();
  }

  log(entry: LogEntry): void {
    this.buffer.push(entry);

    // Flush se buffer estiver cheio
    if (this.buffer.length >= this.batchSize) {
      this.flush();
    }
  }

  private async flush(): Promise<void> {
    if (this.buffer.length === 0) return;

    const entriesToSend = [...this.buffer];
    this.buffer = [];

    try {
      await fetch(this.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: safeStringify({ logs: entriesToSend }),
        // Não aguardar resposta para não bloquear
        keepalive: true,
      });
    } catch (error) {
      console.warn("🚨 [LOGGER] Failed to send logs to remote:", error);
      // Re-adiciona ao buffer em caso de falha
      this.buffer.unshift(...entriesToSend);
    }
  }

  private startPeriodicFlush(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush(); // Flush final
  }
}

class Logger {
  private static instance: Logger;
  private transports: LogTransport[] = [];
  private context: string = "";
  private minLevel: LogLevel = LogLevel.INFO;

  private constructor() {
    // Adiciona transport padrão do console
    this.addTransport(new ConsoleTransport());

    // Adiciona transport remoto em produção
    if (
      process.env.NODE_ENV === "production" &&
      process.env.REACT_APP_LOG_ENDPOINT
    ) {
      this.addTransport(
        new RemoteTransport(process.env.REACT_APP_LOG_ENDPOINT),
      );
    }

    // Define nível mínimo baseado no ambiente
    if (process.env.NODE_ENV === "development") {
      this.minLevel = LogLevel.DEBUG;
    }
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  addTransport(transport: LogTransport): void {
    this.transports.push(transport);
  }

  setContext(context: string): void {
    this.context = context;
  }

  setMinLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    data?: any,
    error?: Error,
  ): LogEntry {
    return {
      level,
      message,
      timestamp: new Date(),
      context: this.context,
      userId: this.getUserId(),
      sessionId: this.getSessionId(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      data,
      error,
      stack: error?.stack,
    };
  }

  private getUserId(): string | undefined {
    try {
      const user = localStorage.getItem("user");
      if (user) {
        const parsed = JSON.parse(user);
        return parsed.id || parsed.uuid;
      }
    } catch {
      // Ignore parse errors
    }
    return undefined;
  }

  private getSessionId(): string | undefined {
    return sessionStorage.getItem("sessionId") || undefined;
  }

  private log(
    level: LogLevel,
    message: string,
    data?: any,
    error?: Error,
  ): void {
    if (!this.shouldLog(level)) return;

    const entry = this.createLogEntry(level, message, data, error);

    this.transports.forEach((transport) => {
      try {
        transport.log(entry);
      } catch (transportError) {
        console.error("🚨 [LOGGER] Transport failed:", transportError);
      }
    });
  }

  debug(message: string, data?: any): void {
    this.log(LogLevel.DEBUG, message, data);
  }

  info(message: string, data?: any): void {
    this.log(LogLevel.INFO, message, data);
  }

  warn(message: string, data?: any): void {
    this.log(LogLevel.WARN, message, data);
  }

  error(message: string, error?: Error, data?: any): void {
    this.log(LogLevel.ERROR, message, data, error);
  }

  fatal(message: string, error?: Error, data?: any): void {
    this.log(LogLevel.FATAL, message, data, error);
  }

  // Métodos de conveniência para operações comuns
  apiCall(endpoint: string, method: string, duration?: number): void {
    this.info(`API Call: ${method} ${endpoint}`, {
      endpoint,
      method,
      duration,
    });
  }

  apiError(
    endpoint: string,
    method: string,
    statusCode: number,
    error?: Error,
  ): void {
    this.error(`API Error: ${method} ${endpoint} (${statusCode})`, error, {
      endpoint,
      method,
      statusCode,
    });
  }

  userAction(action: string, details?: any): void {
    this.info(`User Action: ${action}`, details);
  }

  performance(metric: string, value: number, details?: any): void {
    this.info(`Performance: ${metric}`, { ...details, value });
  }

  // Método para criar logger com contexto específico
  createChildLogger(context: string): Logger {
    const childLogger = new Logger();
    childLogger.setContext(`${this.context}:${context}`);
    childLogger.transports = this.transports;
    childLogger.minLevel = this.minLevel;
    return childLogger;
  }
}

// Exporta instância singleton
export const logger = Logger.getInstance();

// Tipos já exportados acima com as interfaces
